import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import {
  addCursor,
  subpointCursor,
} from '../../../cross-team-shared/cursors/custom-cursors';
import {
  getCssProperty,
  setCssProperty,
} from '../../../cross-team-shared/misc-utils';
import { VectorLayerEditMode } from '../../../cross-team-shared/types/tool-modes';
import { getShapeHelper } from '../shape-helpers/get-shape-helper';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { getCursor } from './vector-renderer-css-property-utils';
import { VectorRendererComponent } from './vector-renderer.component';

type DraggingStatus = 'waiting' | 'dragging' | 'completed';

@Injectable()
export class VectorRendererCursorCssPropertyService {
  draggingStatus$ = new Subject<DraggingStatus>();

  private store = inject(AbstractSensorContentStore2D);
  private cursorBeforeDragging: string | null = null;

  manageCssProperties(vectorRenderer: VectorRendererComponent) {
    this.setUpCustomCursorSubscriptions(vectorRenderer);
    this.setUpDraggingCursorsSubscriptions(vectorRenderer);
    this.setUpEditModeCssSubscriptions(vectorRenderer);

    this.setCssProperty(
      '--h3-subpoint-add-area-cursor',
      addCursor,
      vectorRenderer,
    );
  }

  private setUpCustomCursorSubscriptions(
    vectorRenderer: VectorRendererComponent,
  ) {
    vectorRenderer.subscribeTo(this.getCursorStyle(), (cursor) =>
      this.setCssProperty('cursor', cursor, vectorRenderer),
    );
  }

  private setUpDraggingCursorsSubscriptions(
    vectorRenderer: VectorRendererComponent,
  ) {
    vectorRenderer.subscribeTo(
      this.draggingStatus$.pipe(
        map((draggingStatus) => {
          switch (draggingStatus) {
            case 'waiting':
              this.cursorBeforeDragging = getCssProperty(
                vectorRenderer.elementRef,
                'cursor',
              );
              return 'grab';
            case 'dragging':
              return 'grabbing';
            case 'completed':
              return this.cursorBeforeDragging;
          }
        }),
      ),
      (cursor) => {
        this.setCssProperty('cursor', cursor, vectorRenderer);
      },
    );
  }

  private getCursorStyle() {
    const selectedTool$ = this.store('selectedTool').$.pipe(
      distinctUntilChanged(),
      filter(isDefined),
    );
    return combineLatest([
      selectedTool$,
      this.store('editMode').$.pipe(distinctUntilChanged()),
      this.getAllowsAddingAndRemovingSubpoints$(),
      this.store('shapeCreationEnabled').$,
    ]).pipe(
      map(
        ([
          selectedTool,
          editMode,
          allowsAddingAndRemovingSubpoints,
          shapeCreationEnabled,
        ]) =>
          getCursor({
            selectedTool,
            editMode,
            allowsAddingAndRemovingSubpoints,
            shapeCreationEnabled,
          }),
      ),
    );
  }

  private getAllowsAddingAndRemovingSubpoints$() {
    return this.store.focusedShapeStore$.pipe(
      map((focusedShapeStore) => {
        const focusedShape = focusedShapeStore?.state();
        if (!focusedShape) {
          return false;
        }

        return getShapeHelper(focusedShape.type)
          .allowsAddingAndRemovingSubpoints;
      }),
      distinctUntilChanged(),
    );
  }

  private setUpEditModeCssSubscriptions(
    vectorRenderer: VectorRendererComponent,
  ) {
    const editMode$ = this.store('editMode').$.pipe(distinctUntilChanged());
    vectorRenderer.subscribeTo(editMode$, (editMode) => {
      const drag = editMode === 'drag';
      this.setSubpointCursors(editMode, vectorRenderer);

      this.setCssProperty(
        '--h3-shape-cursor',
        drag ? 'move' : null,
        vectorRenderer,
      );
    });
  }

  private setSubpointCursors(
    editMode: VectorLayerEditMode,
    vectorRenderer: VectorRendererComponent,
  ) {
    const showSubpointCursor = editMode === 'drag';
    const clicky = editMode === 'clicky';

    this.setCssProperty(
      '--h3-subpoint-cursor',
      showSubpointCursor ? subpointCursor : null,
      vectorRenderer,
    );

    this.setCssProperty(
      '--h3-polygon-subpoint-cursor',
      showSubpointCursor || clicky ? subpointCursor : null,
      vectorRenderer,
    );
    return;
  }

  private setCssProperty(
    name: string,
    value: string | number | null,
    vectorRenderer: VectorRendererComponent,
  ) {
    setCssProperty(vectorRenderer.elementRef, name, value);
  }
}
